/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file mailCollabStore.js
 * @description
 * Tiny module-level store for Happy Mailer shared-mailbox collaboration (assign / team tags /
 * replied-by / snooze). Port of the admin `hooks/mail/mailCollabStore.js` + `useMailCollab.js`.
 *
 * Why not React context: the data is a read-through cache of server state that several unrelated
 * mail components (list, rows, reader, settings) AND the realtime listener in `MailContext` all
 * touch. A subscribe/snapshot store keeps `MailContext` untouched except for the socket hook-up.
 *
 * Thread states are keyed `states[address][conversationKey]`; `null` means "looked up, no shared
 * state yet" so rows are not re-requested on every render. Refreshes never clear previous values
 * (no flicker). Nothing is persisted (state is cheap to re-fetch and may contain colleague data);
 * the store is rebound to the signed-in user so a user switch can never show another user's data.
 */

import {useEffect, useMemo, useState, useSyncExternalStore} from 'react';
import * as mailApi from '../services/mailApi';
import {getUserInfo} from './storage';
import {buildSnoozeIndex} from './mailCollabUtils';
import {applyChatEventToState, clearChatUnread} from './mailThreadChat';

const STATE_TTL_MS = 60 * 1000;
const STATE_BATCH = 200;
const STATE_DEBOUNCE_MS = 150;

const EMPTY_TAGS = {personal: [], team: []};

const initialSnapshot = () => ({
    mailboxes: [],
    mailboxesLoaded: false,
    tags: EMPTY_TAGS,
    snoozes: [],
    snoozesLoaded: false,
    states: {},
    viewCounts: {},
    stateVersion: 0,
    meId: '',
});

let snapshot = initialSnapshot();

const listeners = new Set();
const requestedAt = new Map();
const pendingKeys = new Map();
const pendingTimers = new Map();
let boundUserId = '';
let bootstrapPromise = null;

const lower = (value) => String(value || '').trim().toLowerCase();

const emit = (patch) => {
    snapshot = {...snapshot, ...patch};
    listeners.forEach((listener) => listener());
};

/**
 * Subscribes to store changes (for `useSyncExternalStore`).
 *
 * @param {Function} listener Change callback.
 * @returns {Function} Unsubscribe.
 */
export const subscribe = (listener) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
};

/**
 * Current immutable snapshot.
 *
 * @returns {Object} Snapshot.
 */
export const getSnapshot = () => snapshot;

const warn = (label, error) => {
    // eslint-disable-next-line no-console
    console.warn(`Mail collaboration: ${label} failed:`, error?.message || error);
};

/**
 * Drops every cached value (sign-out / user switch).
 */
export const resetCollabStore = () => {
    requestedAt.clear();
    pendingKeys.clear();
    pendingTimers.forEach((timer) => globalThis.clearTimeout(timer));
    pendingTimers.clear();
    bootstrapPromise = null;
    boundUserId = '';
    emit(initialSnapshot());
};

/**
 * Loads the caller's shared + personal mailboxes (`GET /mail/collab/mailboxes`). A failure leaves
 * the list empty, which hides every collaboration UI (older backends keep working).
 *
 * @returns {Promise<Object[]>} Mailboxes.
 */
export const loadMailboxes = async () => {
    try {
        const response = await mailApi.collabListMailboxes();
        const mailboxes = Array.isArray(response?.mailboxes) ? response.mailboxes : [];

        emit({mailboxes, mailboxesLoaded: true});

        return mailboxes;
    } catch (error) {
        warn('mailboxes', error);
        emit({mailboxesLoaded: true});

        return snapshot.mailboxes;
    }
};

/**
 * Loads personal + team tags with counts (`GET /mail/tags`).
 *
 * @returns {Promise<{personal: Object[], team: Object[]}>} Tags.
 */
export const loadTags = async () => {
    try {
        const response = await mailApi.listTags();
        const tags = {
            personal: Array.isArray(response?.personal) ? response.personal : [],
            team: Array.isArray(response?.team) ? response.team : [],
        };

        emit({tags});

        return tags;
    } catch (error) {
        warn('tags', error);

        return snapshot.tags;
    }
};

/**
 * Loads the caller's pending snoozes (`GET /mail/snoozes?status=pending`).
 *
 * @returns {Promise<Object[]>} Snoozes.
 */
export const loadSnoozes = async () => {
    try {
        const response = await mailApi.listSnoozes({status: 'pending'});
        const snoozes = Array.isArray(response?.snoozes) ? response.snoozes : [];

        emit({snoozes, snoozesLoaded: true});

        return snoozes;
    } catch (error) {
        warn('snoozes', error);
        emit({snoozesLoaded: true});

        return snapshot.snoozes;
    }
};

/**
 * Loads the badge counts of a shared mailbox's smart views (assigned to me / done).
 *
 * @param {string} address Shared mailbox address.
 * @returns {Promise<{mine: number, done: number}>} Counts.
 */
export const loadViewCounts = async (address) => {
    const addr = lower(address);

    if (!addr) {
        return {mine: 0, done: 0};
    }

    try {
        const [mine, done] = await Promise.all([
            mailApi.collabListAssignedThreads({address: addr, assignee: 'me', status: 'open'}),
            mailApi.collabListAssignedThreads({address: addr, status: 'done'}),
        ]);
        const counts = {
            mine: (mine?.conversationKeys || []).length,
            done: (done?.conversationKeys || []).length,
        };

        emit({viewCounts: {...snapshot.viewCounts, [addr]: counts}});

        return counts;
    } catch (error) {
        warn('view counts', error);

        return snapshot.viewCounts[addr] || {mine: 0, done: 0};
    }
};

/**
 * Merges one thread state into the map and notifies subscribers.
 *
 * @param {string} address Mailbox address.
 * @param {string} conversationKey Conversation key.
 * @param {Object|null} state Thread state.
 */
export const mergeThreadState = (address, conversationKey, state) => {
    const addr = lower(address);
    const key = lower(conversationKey);

    if (!addr || !key) {
        return;
    }

    requestedAt.set(`${addr}|${key}`, Date.now());
    emit({
        states: {...snapshot.states, [addr]: {...(snapshot.states[addr] || {}), [key]: state || null}},
        stateVersion: snapshot.stateVersion + 1,
    });
};

const flushStates = async (address) => {
    const keys = [...(pendingKeys.get(address) || [])];

    pendingKeys.delete(address);
    pendingTimers.delete(address);

    for (let index = 0; index < keys.length; index += STATE_BATCH) {
        const chunk = keys.slice(index, index + STATE_BATCH);

        try {
            const response = await mailApi.collabGetThreadStates(address, chunk);
            const states = response?.states || {};
            const next = {...(snapshot.states[address] || {})};
            const now = Date.now();

            chunk.forEach((key) => {
                next[key] = states[key] || null;
                requestedAt.set(`${address}|${key}`, now);
            });
            emit({
                states: {...snapshot.states, [address]: next},
                stateVersion: snapshot.stateVersion + 1,
            });
        } catch (error) {
            warn('thread states', error);
        }
    }
};

/**
 * Requests the thread states of the visible rows of ONE shared mailbox. Debounced, deduped and
 * batched (<= 200 keys per call); keys looked up within the last minute are skipped.
 *
 * @param {string} address Shared mailbox address.
 * @param {string[]} conversationKeys Visible rows' conversation keys.
 * @param {{force?: boolean}} [options] `force` ignores the freshness window.
 */
export const requestThreadStates = (address, conversationKeys, options = {}) => {
    const addr = lower(address);

    if (!addr || !Array.isArray(conversationKeys) || !conversationKeys.length) {
        return;
    }

    const now = Date.now();
    const queue = pendingKeys.get(addr) || new Set();

    conversationKeys.map(lower).filter(Boolean).forEach((key) => {
        const seen = requestedAt.get(`${addr}|${key}`);

        if (options.force || !seen || now - seen > STATE_TTL_MS) {
            queue.add(key);
        }
    });

    if (!queue.size) {
        return;
    }

    pendingKeys.set(addr, queue);

    if (!pendingTimers.has(addr)) {
        pendingTimers.set(addr, globalThis.setTimeout(() => flushStates(addr), STATE_DEBOUNCE_MS));
    }
};

/**
 * Forces a refresh of every thread state already in the map (e.g. after sending a reply: the
 * backend records "replied by" asynchronously). Previous values stay visible until fresh ones arrive.
 *
 * @param {number} [delayMs] Delay before refreshing.
 */
export const refreshLoadedStates = (delayMs = 1500) => {
    globalThis.setTimeout(() => {
        Object.entries(snapshot.states).forEach(([address, map]) => {
            requestThreadStates(address, Object.keys(map), {force: true});
        });
    }, delayMs);
};

/**
 * Applies a realtime `mail:thread_state` event and refreshes the smart-view counts of that mailbox.
 *
 * @param {{address: string, conversationKey: string, state: Object}} payload Socket payload.
 */
export const applyThreadStateEvent = (payload) => {
    if (!payload?.address || !payload?.conversationKey) {
        return;
    }

    mergeThreadState(payload.address, payload.conversationKey, payload.state);
    loadViewCounts(payload.address);
    loadTags();
};

const chatListeners = new Set();

/**
 * Subscribes to realtime `mail:thread_chat` events (the open discussion sheet refetches on them).
 *
 * @param {(event: {address: string, conversationKey: string, messageId: string, authorId: string}) => void} listener Callback.
 * @returns {Function} Unsubscribe.
 */
export const subscribeThreadChat = (listener) => {
    chatListeners.add(listener);

    return () => chatListeners.delete(listener);
};

/**
 * Applies a realtime `mail:thread_chat` event: bumps the comment / unread badge of the thread
 * state instantly, schedules an authoritative state refresh and wakes the open discussion sheet.
 * The payload carries no message body (privacy), so the sheet refetches.
 *
 * @param {{address: string, conversationKey: string, messageId?: string, authorId?: string}} payload Socket payload.
 */
export const applyThreadChatEvent = (payload) => {
    const addr = lower(payload?.address);
    const key = lower(payload?.conversationKey);

    if (!addr || !key) {
        return;
    }

    mergeThreadState(addr, key, applyChatEventToState(snapshot.states[addr]?.[key] || null, payload, snapshot.meId));
    requestThreadStates(addr, [key], {force: true});
    chatListeners.forEach((listener) => {
        try {
            listener({address: addr, conversationKey: key, messageId: String(payload.messageId || ''), authorId: String(payload.authorId || '')});
        } catch (error) {
            warn('chat listener', error);
        }
    });
};

/**
 * Clears the unread badge of a thread locally (after the discussion was opened / marked read).
 *
 * @param {string} address Mailbox address.
 * @param {string} conversationKey Conversation key.
 */
export const markThreadChatReadLocal = (address, conversationKey) => {
    const addr = lower(address);
    const key = lower(conversationKey);
    const current = snapshot.states[addr]?.[key] || null;
    const next = clearChatUnread(current);

    if (next && next !== current) {
        mergeThreadState(addr, key, next);
    }
};

/**
 * Looks up the cached state of a thread.
 *
 * @param {string} address Mailbox address.
 * @param {string} conversationKey Conversation key.
 * @returns {Object|null} Thread state.
 */
export const getThreadState = (address, conversationKey) => snapshot.states[lower(address)]?.[lower(conversationKey)] || null;

/**
 * Whether an address is one of the caller's shared mailboxes.
 *
 * @param {string} address Mailbox address.
 * @returns {Object|null} Mailbox when shared and active.
 */
export const getSharedMailbox = (address) => {
    const addr = lower(address);

    return snapshot.mailboxes.find((mailbox) => mailbox.shared && mailbox.isActive !== false && lower(mailbox.address) === addr) || null;
};

const runThreadAction = async (call, address, conversationKey) => {
    const state = await call();

    mergeThreadState(address, conversationKey, state);
    loadViewCounts(address);
    loadTags();

    return state;
};

/**
 * Assigns a thread. Rejects with `error.status === 409` (+ `error.payload.currentAssigneeId`) when
 * somebody else owns the thread and `takeover` was not set.
 *
 * @param {string} address Shared mailbox address.
 * @param {string} conversationKey Conversation key.
 * @param {string} assigneeId Colleague id.
 * @param {{takeover?: boolean, note?: string, subject?: string}} [options] Options.
 * @returns {Promise<Object>} Updated thread state.
 */
export const assignThread = (address, conversationKey, assigneeId, options = {}) => runThreadAction(
    () => mailApi.collabAssignThread({address, conversationKey, assigneeId, ...options}),
    address,
    conversationKey,
);

/**
 * Unassigns a thread.
 *
 * @param {string} address Shared mailbox address.
 * @param {string} conversationKey Conversation key.
 * @param {{subject?: string}} [options] Options.
 * @returns {Promise<Object>} Updated thread state.
 */
export const unassignThread = (address, conversationKey, options = {}) => runThreadAction(
    () => mailApi.collabUnassignThread({address, conversationKey, ...options}),
    address,
    conversationKey,
);

/**
 * Marks a thread done.
 *
 * @param {string} address Shared mailbox address.
 * @param {string} conversationKey Conversation key.
 * @param {{subject?: string}} [options] Options.
 * @returns {Promise<Object>} Updated thread state.
 */
export const markThreadDone = (address, conversationKey, options = {}) => runThreadAction(
    () => mailApi.collabMarkDone({address, conversationKey, ...options}),
    address,
    conversationKey,
);

/**
 * Re-opens a done thread.
 *
 * @param {string} address Shared mailbox address.
 * @param {string} conversationKey Conversation key.
 * @param {{subject?: string}} [options] Options.
 * @returns {Promise<Object>} Updated thread state.
 */
export const reopenThread = (address, conversationKey, options = {}) => runThreadAction(
    () => mailApi.collabReopenThread({address, conversationKey, ...options}),
    address,
    conversationKey,
);

/**
 * Replaces the team tags of a thread.
 *
 * @param {string} address Shared mailbox address.
 * @param {string} conversationKey Conversation key.
 * @param {string[]} tags Team tag ids.
 * @param {{subject?: string}} [options] Options.
 * @returns {Promise<Object>} Updated thread state.
 */
export const setThreadTags = (address, conversationKey, tags, options = {}) => runThreadAction(
    () => mailApi.collabSetThreadTags({address, conversationKey, tags, ...options}),
    address,
    conversationKey,
);

/**
 * Snoozes ("notify me again later") a conversation and refreshes the pending list.
 *
 * @param {{messageId?: string, conversationKey?: string, until?: string, preset?: string, hours?: number, timezone?: string, reason?: string}} payload Snooze request.
 * @returns {Promise<Object>} Created snooze.
 */
export const snoozeThread = async (payload) => {
    const created = await mailApi.snoozeThread(payload);

    await loadSnoozes();

    return created;
};

/**
 * Cancels a pending snooze.
 *
 * @param {string} id Snooze id.
 * @returns {Promise<void>} Resolves when removed and the list was refreshed.
 */
export const cancelSnooze = async (id) => {
    await mailApi.cancelSnooze(id);
    await loadSnoozes();
};

/**
 * Realtime `mail:snooze_due`: the conversation is due again - refresh the pending list so it
 * stops being hidden.
 */
export const applySnoozeDueEvent = () => {
    loadSnoozes();
};

/**
 * Fetches the include/exclude key filter of a smart view (see `mailCollabUtils.buildAssignedQuery`).
 *
 * @param {Object} query Query for `/collab/threads/assigned`.
 * @returns {Promise<Object>} `{mode, conversationKeys, truncated}`.
 */
export const fetchAssignedKeys = (query) => mailApi.collabListAssignedThreads(query);

/**
 * Loads everything the list / settings need once (mailboxes -> tags/snoozes/counts). Rebinds the
 * store when the signed-in user changed since the last call.
 *
 * @returns {Promise<void>} Resolves when loaded.
 */
export const bootstrap = async () => {
    let me = '';

    try {
        me = String((await getUserInfo())?._id || '');
    } catch (_e) {
        me = '';
    }

    if (boundUserId && me && boundUserId !== me) {
        resetCollabStore();
    }
    boundUserId = me || boundUserId;
    if (me && snapshot.meId !== me) {
        emit({meId: me});
    }

    const mailboxes = await loadMailboxes();

    await Promise.all([
        loadTags(),
        loadSnoozes(),
        ...mailboxes.filter((mailbox) => mailbox.shared).map((mailbox) => loadViewCounts(mailbox.address)),
    ]);
};

/**
 * Refreshes everything after a push of a collaboration event (`mail.assigned` …) and re-requests
 * the states already in the map.
 *
 * @returns {Promise<void>} Resolves when refreshed.
 */
export const refreshAll = async () => {
    await bootstrap();
    refreshLoadedStates(0);
};

/**
 * Creates a tag: a personal tag (mailbox label) or a team tag of a shared mailbox (manager only).
 *
 * @param {{scope: 'personal'|'team', address?: string, name: string, color: string}} payload Tag request.
 * @returns {Promise<Object>} API response.
 */
export const createTag = async ({scope, address, name, color}) => {
    const body = {name, color};
    const response = scope === 'team'
        ? await mailApi.collabCreateTeamTag(address, body)
        : await mailApi.createPersonalTag(body);

    await Promise.all([loadTags(), loadMailboxes()]);

    return response;
};

/**
 * Updates a team tag of a shared mailbox (manager only).
 *
 * @param {string} address Shared mailbox address.
 * @param {string} tagId Tag id.
 * @param {{name?: string, color?: string}} patch Changes.
 * @returns {Promise<Object>} API response.
 */
export const updateTeamTag = async (address, tagId, patch) => {
    const response = await mailApi.collabUpdateTeamTag(address, tagId, patch);

    await Promise.all([loadTags(), loadMailboxes()]);

    return response;
};

/**
 * Deletes a team tag of a shared mailbox (manager only).
 *
 * @param {string} address Shared mailbox address.
 * @param {string} tagId Tag id.
 * @returns {Promise<Object>} API response.
 */
export const deleteTeamTag = async (address, tagId) => {
    const response = await mailApi.collabDeleteTeamTag(address, tagId);

    await Promise.all([loadTags(), loadMailboxes()]);

    return response;
};

/**
 * Updates shared-mailbox settings (manager only): name, auto-assign, snooze presets, isActive.
 *
 * @param {string} address Shared mailbox address.
 * @param {Object} patch Changes.
 * @returns {Promise<Object>} API response.
 */
export const updateMailbox = async (address, patch) => {
    const response = await mailApi.collabUpdateMailbox(address, patch);

    await loadMailboxes();

    return response;
};

/**
 * Members of a shared mailbox with role / online / open workload.
 *
 * @param {string} address Shared mailbox address.
 * @returns {Promise<Object[]>} Members.
 */
export const fetchMembers = async (address) => {
    const response = await mailApi.collabListMembers(address);

    return Array.isArray(response?.members) ? response.members : [];
};

/**
 * Activity timeline of a thread.
 *
 * @param {string} address Shared mailbox address.
 * @param {string} conversationKey Conversation key.
 * @returns {Promise<{state: Object|null, activity: Object[]}>} Timeline.
 */
export const fetchActivity = async (address, conversationKey) => {
    const response = await mailApi.collabGetThreadActivity(address, conversationKey);

    return {
        state: response?.state || null,
        activity: Array.isArray(response?.activity) ? response.activity : [],
    };
};

/**
 * React binding of the collaboration store. The first mounted consumer bootstraps the store once;
 * realtime events are applied by `MailContext` through `applyThreadStateEvent` /
 * `applySnoozeDueEvent`.
 *
 * @returns {Object} `{mailboxes, sharedMailboxes, tags, snoozes, snoozeIndex, states, viewCounts,
 * stateVersion, loaded, meId, getState, requestStates, ...actions}`.
 */
export const useMailCollab = () => {
    const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    useEffect(() => {
        if (!bootstrapPromise) {
            bootstrapPromise = bootstrap().catch(() => {
                bootstrapPromise = null;
            });
        }
    }, []);

    const sharedMailboxes = useMemo(
        () => snap.mailboxes.filter((mailbox) => mailbox.shared && mailbox.isActive !== false),
        [snap.mailboxes],
    );
    // Bumped when the earliest pending snooze comes due, so the conversation reappears without
    // waiting for a socket event (the index drops snoozes whose `until` has passed).
    const [dueTick, setDueTick] = useState(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const snoozeIndex = useMemo(() => buildSnoozeIndex(snap.snoozes), [snap.snoozes, dueTick]);

    useEffect(() => {
        const now = Date.now();
        const upcoming = snap.snoozes
            .map((snooze) => new Date(snooze.until).getTime())
            .filter((time) => Number.isFinite(time) && time > now);

        if (!upcoming.length) {
            return undefined;
        }

        // setTimeout caps at ~24.8 days; longer waits simply re-arm on the next tick.
        const delay = Math.min(Math.min(...upcoming) - now + 500, 2147483000);
        const timer = globalThis.setTimeout(() => setDueTick((value) => value + 1), delay);

        return () => globalThis.clearTimeout(timer);
    }, [snap.snoozes, dueTick]);

    return {
        mailboxes: snap.mailboxes,
        sharedMailboxes,
        tags: snap.tags,
        snoozes: snap.snoozes,
        snoozeIndex,
        states: snap.states,
        viewCounts: snap.viewCounts,
        stateVersion: snap.stateVersion,
        loaded: snap.mailboxesLoaded,
        meId: snap.meId,

        getState: getThreadState,
        getSharedMailbox,
        requestStates: requestThreadStates,
        fetchAssignedKeys,

        refreshMailboxes: loadMailboxes,
        refreshTags: loadTags,
        refreshSnoozes: loadSnoozes,
        refreshViewCounts: loadViewCounts,
        refreshAll,

        assign: assignThread,
        unassign: unassignThread,
        markDone: markThreadDone,
        reopen: reopenThread,
        setTags: setThreadTags,
        snooze: snoozeThread,
        cancelSnooze,
        createTag,
        updateTeamTag,
        deleteTeamTag,
        updateMailbox,
        fetchMembers,
        fetchActivity,
    };
};

export default useMailCollab;
